<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import { useConfigStore } from "@/stores/config";
import { useMachinesStore } from "@/stores/machines";
import SessionMosaic from "@/components/SessionMosaic.vue";
import AgentFilterBar from "@/components/AgentFilterBar.vue";
import { useAgentFilter } from "@/composables/useAgentFilter";
import { getPreviewMode, LIVE_PREVIEW_CAP } from "@/prefs";
import { lastSeenLabel } from "@/utils/lastSeen";

const { t, locale } = useI18n();
const router = useRouter();
const { state: configState } = storeToRefs(useConfigStore());
const machines = useMachinesStore();
const filter = useAgentFilter(
  computed(() => machines.agents),
  { syncRoute: true },
);

const previewMode = ref(getPreviewMode());
function onPrefs() {
  previewMode.value = getPreviewMode();
}
// Cap live sockets: past LIVE_PREVIEW_CAP running sessions, fall back to the
// cheap "last" snapshot even when the user picked live.
const runningCount = computed(() => machines.sessions.filter((s) => s.status !== "stopped").length);
const effectivePreview = computed<"live" | "last" | "off">(() => {
  if (previewMode.value === "off") return "off";
  if (previewMode.value === "live" && runningCount.value > LIVE_PREVIEW_CAP) return "last";
  return previewMode.value;
});

onMounted(() => {
  void machines.start();
  window.addEventListener("nyx-prefs", onPrefs);
});

onBeforeUnmount(() => {
  machines.stop();
  window.removeEventListener("nyx-prefs", onPrefs);
});

const config = computed(() =>
  configState.value.kind === "ready" ? configState.value.config : null,
);

function presenceLabel(p?: string) {
  return p === "online" ? t("home.online") : t("home.offline");
}

function presenceTone(p?: string) {
  return p === "online" ? "ok" : "bad";
}

function offlineSince(a: { presence?: string; last_seen_at?: string | null }) {
  if (a.presence === "online") return null;
  const rel = lastSeenLabel(a.last_seen_at, locale.value);
  return rel ? t("home.last_seen", { rel }) : null;
}

function startSession(agentId: string) {
  router.push({ name: "new-session", query: { agent_id: agentId } });
}

function attachSession(sessionId: string) {
  router.push({ name: "session", params: { sessionId } });
}

// §3.2: connect to a machine via a one-time access code.
const code = ref("");
const redeeming = ref(false);
const codeError = ref<string | null>(null);
async function redeemCode() {
  const c = code.value.trim();
  if (!c || redeeming.value) return;
  redeeming.value = true;
  codeError.value = null;
  try {
    const { session_id } = await api.consent.redeem(c);
    code.value = "";
    router.push({ name: "session", params: { sessionId: session_id } });
  } catch (e) {
    codeError.value = describeError(e, t);
  } finally {
    redeeming.value = false;
  }
}
</script>

<template>
  <section class="home">
    <header class="home__header">
      <h1>{{ t("home.welcome") }}</h1>
      <span class="admin-badge" :data-tone="machines.state.kind === 'ready' ? 'ok' : 'info'">{{
        machines.state.kind === "ready" ? t("home.stream_live") : t("home.stream_loading")
      }}</span>
    </header>

    <form class="home__code" @submit.prevent="redeemCode">
      <input
        v-model="code"
        :disabled="redeeming"
        :placeholder="t('home.code_placeholder')"
        spellcheck="false"
        autocapitalize="off"
      />
      <button type="submit" class="home__code-btn" :disabled="redeeming || !code.trim()">
        {{ redeeming ? "…" : t("home.code_connect") }}
      </button>
      <span v-if="codeError" class="admin-error home__code-err">{{ codeError }}</span>
    </form>

    <p v-if="machines.state.kind === 'error'" class="admin-error">
      {{ machines.state.message }}
    </p>

    <div
      v-else-if="machines.state.kind !== 'ready'"
      class="loading-note"
      role="status"
      aria-live="polite"
    >
      <span class="spinner" aria-hidden="true"></span>
      {{ t("home.stream_loading") }}
    </div>

    <div v-if="machines.agents.length === 0 && machines.state.kind === 'ready'" class="admin-empty">
      {{ t("home.no_machines") }}
    </div>

    <AgentFilterBar v-if="machines.agents.length" :filter="filter" show-group />
    <p
      v-if="machines.agents.length && filter.filtered.length === 0"
      class="admin-empty"
      data-test="machines-no-results"
    >
      {{ t("home.no_results") }}
    </p>

    <template v-for="group in filter.groups" :key="group.key || '*'">
      <h2 v-if="filter.groupBy" class="machines__group">
        {{ filter.groupBy }}: {{ group.key || t("filter.untagged") }}
        <span class="machines__group-count">{{ group.agents.length }}</span>
      </h2>
      <ul class="machines">
        <li
          v-for="agent in group.agents"
          :key="agent.id"
          class="machine"
          :data-test="`machine-${agent.id}`"
        >
          <div class="machine__head">
            <strong class="machine__name">{{ agent.display_name || agent.hostname }}</strong>
            <span
              class="admin-badge"
              :data-tone="presenceTone(agent.presence)"
              :title="offlineSince(agent) ?? undefined"
            >
              {{ presenceLabel(agent.presence) }}
            </span>
            <small v-if="offlineSince(agent)" class="machine__last-seen">{{
              offlineSince(agent)
            }}</small>
          </div>
          <div v-if="agent.tags?.length" class="tags machine__tags">
            <button
              v-for="tag in agent.tags"
              :key="tag"
              type="button"
              class="tag"
              :data-active="filter.tags.includes(tag) ? 'true' : undefined"
              @click="filter.toggleTag(tag)"
            >
              {{ tag }}
            </button>
          </div>
          <dl class="kv">
            <dt>{{ t("home.machine_login") }}</dt>
            <dd>{{ agent.login }}</dd>
            <dt>{{ t("home.machine_host") }}</dt>
            <dd>{{ agent.hostname }}</dd>
            <dt>{{ t("home.machine_version") }}</dt>
            <dd>{{ agent.version ?? "—" }}</dd>
            <dt>{{ t("home.machine_sessions") }}</dt>
            <dd>{{ machines.sessionsForAgent(agent.id).length }}</dd>
          </dl>

          <ul v-if="machines.sessionsForAgent(agent.id).length" class="sessions">
            <li v-for="s in machines.sessionsForAgent(agent.id)" :key="s.id" class="sessions__item">
              <button type="button" class="sessions__attach" @click="attachSession(s.id)">
                <span class="sessions__engine">{{ s.name || s.engine }}</span>
                <span
                  class="sessions__status"
                  :data-tone="s.status === 'running' ? 'ok' : 'warn'"
                  >{{ s.status }}</span
                >
                <span class="sessions__go">{{ t("home.attach") }} →</span>
              </button>
              <SessionMosaic
                v-if="effectivePreview !== 'off' && s.status === 'running'"
                :key="`${s.id}-${effectivePreview}-${s.cols}x${s.rows}`"
                :session-id="s.id"
                :target-type="s.target_type"
                :mode="effectivePreview"
                :cols="s.cols"
                :rows="s.rows"
                class="sessions__preview"
                role="button"
                tabindex="0"
                @click="attachSession(s.id)"
              />
            </li>
          </ul>
          <p v-else class="sessions__empty">{{ t("home.no_active_sessions") }}</p>

          <button
            type="button"
            class="machine__action"
            :disabled="agent.presence !== 'online' || agent.status !== 'enabled'"
            @click="startSession(agent.id)"
          >
            {{ t("home.start_shell") }}
          </button>
        </li>
      </ul>
    </template>

    <section v-if="config" class="cluster">
      <h2>{{ t("home.cluster_title") }}</h2>
      <dl class="kv">
        <dt>{{ t("home.mode_label") }}</dt>
        <dd>{{ t(`mode.${config.mode}`) }}</dd>
        <dt>{{ t("home.version_label") }}</dt>
        <dd>{{ config.version }}</dd>
        <dt>{{ t("home.server_time_label") }}</dt>
        <dd>{{ config.server_time }}</dd>
      </dl>
    </section>
  </section>
</template>

<style scoped>
.home {
  max-width: 64rem;
}
.home__header {
  display: flex;
  align-items: baseline;
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.home__code {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.home__code input {
  flex: 0 1 16rem;
  min-width: 9rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: var(--font-mono);
}
.home__code-btn {
  padding: 0.45rem 0.9rem;
  border: none;
  border-radius: 6px;
  background: var(--color-accent);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}
.home__code-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.home__code-err {
  font-size: 0.8rem;
}
.machines__group {
  margin: 1rem 0 0.5rem;
  font-size: 0.9rem;
  color: var(--color-fg-dim);
  text-transform: none;
}
.machines__group-count {
  margin-left: 0.4rem;
  font-size: 0.75rem;
  opacity: 0.7;
}
.machine__last-seen {
  color: var(--color-fg-dim);
  font-size: 0.8em;
  margin-left: auto;
}

.machine__tags {
  margin: 0.25rem 0 0.5rem;
}
.machines {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}
.machine {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-elev);
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.machine__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.machine__name {
  font-size: 1rem;
}
.sessions {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.sessions__item {
  margin: 0;
}
.sessions__attach {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.6rem;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  color: var(--color-fg);
  cursor: pointer;
  text-align: left;
}
.sessions__attach:hover {
  border-color: var(--color-accent);
}
.sessions__engine {
  font-weight: 600;
}
.sessions__status {
  text-transform: lowercase;
}
.sessions__status[data-tone="ok"] {
  color: var(--color-ok);
}
.sessions__status[data-tone="warn"] {
  color: var(--color-warn);
}
.sessions__go {
  margin-left: auto;
  color: var(--color-fg-dim);
}
.sessions__preview {
  margin-top: 0.35rem;
  cursor: pointer;
}
.sessions__empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-fg-dim);
}
.machine__action {
  align-self: flex-start;
  padding: 0.4rem 0.85rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
}
.machine__action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.cluster {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border);
}
.cluster h2 {
  font-size: 1rem;
  margin: 0 0 0.6rem;
  color: var(--color-fg-dim);
}
</style>
